import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkflowSaving } from '../hooks/useWorkflowSaving';
import { loadWorkflows, setCurrentWorkflow, setUnsavedChanges, updateWorkflow, deleteWorkflow } from '../../../../redux/actions/async/agentBuilder';
import { I } from '../../../../common/v5/config';
import './WorkflowToolbar.css';
import { UnnestedAreaSingleSelect } from '../../../../reactcomponents/Dropdown';
import { ModalBox } from '../../../../reactcomponents/Modal';
import { TextInputRow } from '../../../../reactcomponents/Form';


const WorkflowToolbar = ({ 
  workflows = [],
  onWorkflowChange,
  onNodesChange,
  onConnectionsChange,
  areaList = [],
  className = ''
}) => {
  const dispatch = useDispatch();
  const [showWorkflowPicker, setShowWorkflowPicker] = useState(false);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsDescription, setSaveAsDescription] = useState('');
  const [saveAsArea, setSaveAsArea] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Area dropdown state (handled internally by UnnestedAreaSingleSelect)
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  
  // Edit workflow dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editArea, setEditArea] = useState('');
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);

  const {
    isNewWorkflow,
    isSaving,
    saveError,
    isDirty,
    saveAsNewWorkflow,
    updateExistingWorkflow,
    saveCurrentWorkflow,
    startAutoSave,
    stopAutoSave,
    currentWorkflow
  } = useWorkflowSaving(areaList);

  const loading = useSelector(state => state.agentBuilder?.loading || false);
  
  // Filter workflows based on search term
  const filteredWorkflows = workflows.filter(workflow => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      workflow.name?.toLowerCase().includes(searchLower) ||
      workflow.description?.toLowerCase().includes(searchLower) ||
      workflow.workflowId?.toLowerCase().includes(searchLower)
    );
  });
  


  // Load workflows when picker opens
  const handleOpenWorkflowPicker = useCallback(async () => {
    try {
      console.log('Load button clicked - loading workflows...');
      const workflows = await dispatch(loadWorkflows());
      console.log('Loaded workflows from action:', workflows);
      setSearchTerm(''); // Reset search when opening modal
      setShowWorkflowPicker(true);
    } catch (error) {
      console.error('Failed to load workflows:', error);
    }
  }, [dispatch]);

  // Load a specific workflow
  const handleLoadWorkflow = useCallback(async (workflow) => {
    try {
      console.log('Loading workflow:', workflow);
      dispatch(setCurrentWorkflow(workflow));
      
      // Set nodes/connections if they exist (they should already be parsed by setCurrentWorkflow)
      if (workflow.nodes) {
        console.log('Loading nodes:', workflow.nodes);
        dispatch({ type: 'AGENT_BUILDER_SET_NODES', payload: workflow.nodes });
        if (onNodesChange) onNodesChange(workflow.nodes);
      }
      
      if (workflow.connections) {
        console.log('Loading connections:', workflow.connections);
        dispatch({ type: 'AGENT_BUILDER_SET_CONNECTIONS', payload: workflow.connections });
        if (onConnectionsChange) onConnectionsChange(workflow.connections);
      }

      dispatch(setUnsavedChanges(false));
      setShowWorkflowPicker(false);
      
      if (onWorkflowChange) {
        onWorkflowChange(workflow);
      }
      console.log('Workflow loaded successfully');
    } catch (error) {
      console.error('Failed to load workflow:', error);
    }
  }, [dispatch, onWorkflowChange, onNodesChange, onConnectionsChange]);

  // Create new workflow
  const handleNewWorkflow = useCallback(() => {
    if (isDirty) {
      if (!window.confirm(I('You have unsaved changes. Are you sure you want to create a new workflow?'))) {
        return;
      }
    }

    // Clear current workflow and canvas
    dispatch(setCurrentWorkflow(null));
    dispatch({ type: 'AGENT_BUILDER_SET_NODES', payload: [] });
    dispatch({ type: 'AGENT_BUILDER_SET_CONNECTIONS', payload: [] });
    dispatch(setUnsavedChanges(false));
    
    // Also clear local state
    if (onNodesChange) onNodesChange([]);
    if (onConnectionsChange) onConnectionsChange([]);
    
    if (onWorkflowChange) {
      onWorkflowChange(null);
    }
  }, [dispatch, isDirty, onWorkflowChange, onNodesChange, onConnectionsChange]);

  // Save current workflow
  const handleSave = useCallback(async () => {
    try {
      console.log('Save button clicked - starting save...');
      const result = await saveCurrentWorkflow();
      console.log('Save completed with result:', result);
    } catch (error) {
      console.error('Save failed:', error);
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to save workflow: ${error.message || 'Unknown error'}`
        }
      });
    }
  }, [saveCurrentWorkflow]);

  // Save as new workflow
  const handleSaveAs = useCallback(async () => {
    if (!saveAsName.trim()) {
      alert(I('Please enter a workflow name'));
      return;
    }
    if (!saveAsArea) {
      alert(I('Please select an area'));
      return;
    }

    try {
      // Save as new workflow with the specified name, description, and area
      const result = await saveCurrentWorkflow({
        name: saveAsName.trim(),
        description: saveAsDescription.trim(),
        area: saveAsArea
      });
      
      if (result) {
        setShowSaveAsDialog(false);
        setSaveAsName('');
        setSaveAsDescription('');
        setSaveAsArea('');
        // Reset area dropdown if it's open
        // The UnnestedAreaSingleSelect handles its own state, so no need to toggle here
      }
    } catch (error) {
      console.error('Save As failed:', error);
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to save workflow as "${saveAsName}": ${error.message || 'Unknown error'}`
        }
      });
    }
  }, [saveAsName, saveAsDescription, saveAsArea, saveCurrentWorkflow]);

  // Context menu handlers
  const handleContextMenu = useCallback((e, workflow) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      workflow: workflow
    });
    setSelectedWorkflow(workflow);
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setSelectedWorkflow(null);
  }, []);

  // Edit workflow handlers
  const handleEditWorkflow = useCallback(() => {
    if (selectedWorkflow) {
      console.log('handleEditWorkflow called with workflow:', selectedWorkflow);
      setEditName(selectedWorkflow.name || '');
      setEditDescription(selectedWorkflow.description || '');
      // Convert area string to integer for the dropdown
      const areaId = selectedWorkflow.area ? parseInt(selectedWorkflow.area, 10) : '';
      setEditArea(areaId);
      setEditingWorkflowId(selectedWorkflow.workflowId);
      console.log('Set edit state:', { name: selectedWorkflow.name, description: selectedWorkflow.description, areaId, workflowId: selectedWorkflow.workflowId });
      setShowEditDialog(true);
      closeContextMenu();
    }
  }, [selectedWorkflow, closeContextMenu]);

  const handleSaveEdit = useCallback(async () => {
    console.log('handleSaveEdit called with:', { editName, editingWorkflowId, selectedWorkflow: selectedWorkflow?.name });
    
    if (!editName.trim()) {
      console.log('Validation failed - empty workflow name');
      alert(I('Please enter a workflow name'));
      return;
    }

    if (!editingWorkflowId) {
      console.log('Validation failed - no workflow ID for editing');
      alert(I('No workflow selected for editing'));
      return;
    }

    if (!editArea || editArea === '') {
      alert(I('Please select an area'));
      return;
    }

    try {
      // Find the workflow by ID from the workflows list
      const workflowToEdit = workflows.find(w => w.workflowId === editingWorkflowId);
      if (!workflowToEdit) {
        alert(I('Workflow not found'));
        return;
      }

      const result = await dispatch(updateWorkflow(editingWorkflowId, {
        name: editName.trim(),
        description: editDescription.trim(),
        // Keep existing data
        workflowId: editingWorkflowId,
        active: workflowToEdit.active,
        nodes: workflowToEdit.nodes,
        connections: workflowToEdit.connections,
        settings: workflowToEdit.settings,
        meta: workflowToEdit.meta,
        tags: workflowToEdit.tags,
        systemGroup: workflowToEdit.systemGroup,
        area: editArea, // Use the edited area
        spc: workflowToEdit.spc
      }));
      
      // Check if the result contains an error even with 200 status
      if (result && result.error) {
        throw new Error(result.error);
      }
      
      setShowEditDialog(false);
      setEditName('');
      setEditDescription('');
      setEditArea('');
      setEditingWorkflowId(null);
      setSelectedWorkflow(null);
    } catch (error) {
      console.error('Edit workflow failed:', error);
      alert(I('Failed to update workflow: ') + error.message);
    }
  }, [editName, editDescription, editArea, editingWorkflowId, workflows, dispatch]);

  // Delete workflow handler
  const handleDeleteWorkflow = useCallback(async () => {
    if (!selectedWorkflow) return;
    
    const confirmDelete = window.confirm(
      I('Are you sure you want to delete the workflow "') + selectedWorkflow.name + '"?\n\n' +
      I('This action cannot be undone.')
    );
    
    if (confirmDelete) {
      try {
        await dispatch(deleteWorkflow(selectedWorkflow.workflowId));
        closeContextMenu();
        
        // If we deleted the current workflow, clear it
        if (currentWorkflow?.workflowId === selectedWorkflow.workflowId) {
          dispatch(setCurrentWorkflow(null));
        }
      } catch (error) {
        console.error('Delete workflow failed:', error);
        alert(I('Failed to delete workflow: ') + error.message);
      }
    } else {
      closeContextMenu();
    }
  }, [selectedWorkflow, dispatch, closeContextMenu, currentWorkflow]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        closeContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu, closeContextMenu]);

  const currentWorkflowName = currentWorkflow?.name || (isNewWorkflow ? 'New Workflow' : 'Untitled');

  return (
    <div className={`workflow-toolbar ${className}`}>
      <div className="workflow-toolbar-section">
        <div className="workflow-info">
          <span className="workflow-name">{currentWorkflowName}</span>
          {isDirty && <span className="unsaved-indicator">●</span>}
          {saveError && <span className="error-indicator" title={saveError}>⚠</span>}
        </div>
      </div>

      <div className="workflow-toolbar-section">
        <button
          className="toolbar-button new-button"
          onClick={handleNewWorkflow}
          title={I('New Workflow')}
        >
          <i className="fa fa-plus" />
          {I('New')}
        </button>

        <button
          className="toolbar-button load-button"
          onClick={handleOpenWorkflowPicker}
          disabled={loading}
          title={I('Load Workflow')}
        >
          <i className="fa fa-folder-open" />
          {I('Load')}
        </button>

        <button
          className={`toolbar-button save-button ${isDirty ? 'has-changes' : 'saved'}`}
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          title={isDirty ? I('Save (Ctrl+S)') : I('Saved')}
        >
          {isSaving ? (
            <>
              <i className="fa fa-spinner fa-spin" />
              {I('Saving...')}
            </>
          ) : isDirty ? (
            <>
              <i className="fa fa-save" />
              {I('Save')}
            </>
          ) : (
            <>
              <i className="fa fa-check" />
              {I('Saved')}
            </>
          )}
        </button>

        <button
          className="toolbar-button save-as-button"
          onClick={() => setShowSaveAsDialog(true)}
          title={I('Save As...')}
        >
          <i className="fa fa-copy" />
          {I('Save As...')}
        </button>
      </div>

      {/* Workflow Picker Modal - Modern Design */}
      {showWorkflowPicker && (
        <ModalBox
          show={showWorkflowPicker}
          className="workflow-picker-modal"
          onClose={() => setShowWorkflowPicker(false)}
          withOutsideClick={true}
          headerContent={
            <div className="workflow-picker-header">
              <div className="workflow-picker-title">
                <h3 className="modal-title">{I('Load Workflow')}</h3>
              </div>
              <div className="workflow-picker-search">
                <TextInputRow
                  id="workflow-search"
                  placeholder={I('Search workflows...')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          }
        >
          <div className="workflow-picker-content">
            {loading ? (
              <div className="workflow-loading-state">
                <i className="fa fa-spinner fa-spin fa-2x" />
                <h4>{I('Loading workflows...')}</h4>
                <p>{I('Please wait while we fetch your workflows')}</p>
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="workflow-empty-state">
                {searchTerm ? (
                  <>
                    <i className="fa fa-search fa-2x" />
                    <h4>{I('No workflows found')}</h4>
                    <p>{I('Try adjusting your search term')}</p>
                    <button 
                      className="btn btn-outline"
                      onClick={() => setSearchTerm('')}
                    >
                      {I('Clear search')}
                    </button>
                  </>
                ) : (
                  <>
                    <i className="fa fa-sitemap fa-2x" />
                    <h4>{I('No workflows yet')}</h4>
                    <p>{I('Create and save a workflow to see it here')}</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        setShowWorkflowPicker(false);
                        // Create new workflow logic could go here
                      }}
                    >
                      <i className="fa fa-plus" />
                      {I('Create Workflow')}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="workflow-grid">
                {filteredWorkflows.map(workflow => (
                  <div
                    key={workflow.workflowId}
                    className={`workflow-card ${currentWorkflow?.workflowId === workflow.workflowId ? 'current' : ''}`}
                    onClick={() => handleLoadWorkflow(workflow)}
                    onContextMenu={(e) => handleContextMenu(e, workflow)}
                  >
                    <div className="workflow-card-header">
                      <div className="workflow-icon">
                        <i className="fa fa-sitemap" />
                      </div>
                      <div className="workflow-info">
                        <h5 className="workflow-name">{workflow.name}</h5>
                        <span className="workflow-id">ID: {workflow.workflowId?.split('_')[1] || workflow.workflowId}</span>
                      </div>
                      {currentWorkflow?.workflowId === workflow.workflowId && (
                        <div className="current-badge">
                          <i className="fa fa-check-circle" />
                          <span>{I('Current')}</span>
                        </div>
                      )}
                    </div>
                    
                    {workflow.description && (
                      <div className="workflow-description">
                        {workflow.description}
                      </div>
                    )}
                    
                    <div className="workflow-meta">
                      <div className="workflow-updated">
                        <i className="fa fa-clock-o" />
                        <span>
                          {workflow.timestampUpdated 
                            ? new Date(workflow.timestampUpdated * 1000).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : I('No date')
                          }
                        </span>
                      </div>
                      <div className="workflow-actions">
                        <button
                          className="workflow-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, workflow);
                          }}
                          title={I('More actions')}
                        >
                          <i className="fa fa-ellipsis-v" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalBox>
      )}

      {/* Save As Dialog - Improved Design */}
      <ModalBox
        show={showSaveAsDialog}
        className="workflow-save-as-modal"
        onClose={() => setShowSaveAsDialog(false)}
        withOutsideClick={true}
        headerContent={
          <h3 className="modal-title">{I('Save Workflow As')}</h3>
        }
      >
        <div className="workflow-save-form">
          {/* Workflow Name */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Workflow Name")}
            </div>
            <div className="form-input-container">
              <input
                type="text"
                id="workflow-name"
                name="workflowName"
                value={saveAsName}
                onChange={e => setSaveAsName(e.target.value)}
                className="admin-text-input"
                placeholder={I("Enter workflow name...")}
                onKeyDown={e => {
                  if (e.key === 'Enter' && saveAsName.trim() && saveAsArea) {
                    handleSaveAs();
                  } else if (e.key === 'Escape') {
                    setShowSaveAsDialog(false);
                  }
                }}
              />
            </div>
          </div>
          
          {/* Workflow Description */}
          <div className="form-input-with-label-row">
            <div className="form-label">
              {I("Description")} <span className="optional-text">({I("Optional")})</span>
            </div>
            <div className="form-input-container">
              <textarea
                id="workflowDescription"
                name="workflowDescription"
                value={saveAsDescription}
                onChange={e => setSaveAsDescription(e.target.value)}
                className="admin-text-input"
                rows={4}
                placeholder={I("Enter workflow description...")}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setShowSaveAsDialog(false);
                  }
                }}
              />
            </div>
          </div>
          
          {/* Area Selection */}
          <div className="form-input-with-label-row">
            <div className="form-label mandatory">
              {I("Area")}
            </div>
            <div className="form-input-container">
              <UnnestedAreaSingleSelect
                data={areaList || []}
                invalidSelectedText={I('Select area...')}
                onSelect={(areaId) => setSaveAsArea(areaId)}
                selected={saveAsArea}
                nested={{key: 'Areas'}}
                idFields={{ id: "Id", name: "Name" }}
              />
            </div>
          </div>
          
          <div className="modal-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSaveAsDialog(false)}
            >
              {I('Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveAs}
              disabled={!saveAsName.trim() || !saveAsArea}
            >
              {I('Save')}
            </button>
          </div>
        </div>
      </ModalBox>

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10000
          }}
        >
          <div className="context-menu-item" onClick={handleEditWorkflow}>
            <i className="fa fa-edit"></i>
            {I('Edit')}
          </div>
          <div className="context-menu-item delete" onClick={handleDeleteWorkflow}>
            <i className="fa fa-trash"></i>
            {I('Delete')}
          </div>
        </div>
      )}

      {/* Edit Workflow Dialog */}
      {showEditDialog && (
        <ModalBox
          show={showEditDialog}
          className="workflow-edit-modal"
          onClose={() => setShowEditDialog(false)}
          withOutsideClick={true}
          headerContent={
            <h3 className="modal-title">{I('Edit Workflow')}</h3>
          }
        >
          <div className="workflow-edit-form">
            {/* Workflow Name */}
            <div className="form-input-with-label-row">
              <div className="form-label mandatory">
                {I("Workflow Name")}
              </div>
              <div className="form-input-container">
                <input
                  type="text"
                  id="edit-workflow-name"
                  name="editWorkflowName"
                  value={editName}
                  onChange={e => {
                    console.log('Name input onChange:', e.target.value);
                    setEditName(e.target.value);
                  }}
                  className="admin-text-input"
                  placeholder={I("Enter workflow name...")}
                  autoFocus={true}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editName.trim()) {
                      handleSaveEdit();
                    } else if (e.key === 'Escape') {
                      setShowEditDialog(false);
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Workflow Description */}
            <div className="form-input-with-label-row">
              <div className="form-label">
                {I("Description")} <span className="optional-text">({I("Optional")})</span>
              </div>
              <div className="form-input-container">
                <textarea
                  id="editWorkflowDescription"
                  name="editWorkflowDescription"
                  value={editDescription}
                  onChange={e => {
                    console.log('Description input onChange:', e.target.value);
                    setEditDescription(e.target.value);
                  }}
                  className="admin-text-input"
                  rows={4}
                  placeholder={I("Enter workflow description...")}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setShowEditDialog(false);
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Area Selection */}
            <div className="form-input-with-label-row">
              <div className="form-label mandatory">
                {I("Area")}
              </div>
              <div className="form-input-container">
                <UnnestedAreaSingleSelect
                  data={areaList || []}
                  invalidSelectedText={I('Select area...')}
                  onSelect={setEditArea}
                  selected={editArea}
                  nested={{key: 'Areas'}}
                  idFields={{id: "Id", name: "Name"}}
                />
              </div>
            </div>
            <div className="modal-form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEditDialog(false)}
              >
                {I('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={!editName.trim() || !editArea}
              >
                {I('Save Changes')}
              </button>
            </div>
          </div>
        </ModalBox>
      )}
    </div>
  );
};

export default WorkflowToolbar; 
 

 